import { useContext, useState } from "react";
import { ChevronDown, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { PersonPicker } from "@/components/people/person-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ImpersonationContext } from "@/contexts/impersonation-context";
import { useCapabilities } from "@/hooks/use-capabilities";
import { useProfileData } from "@/hooks/use-profile-data";
import { useReturnToSelf } from "@/hooks/use-return-to-self";
import {
  PREVIEW_NOTICE,
  firstName,
  previewingLine,
  setKoryPreview,
  useKoryPreview,
  type PreviewTarget,
} from "@/lib/kory-preview";
import { cn } from "@/lib/utils";

const CENTRAL_TENANT_ID = "hickoryai";

/** True when this caller may open the preview control. The server still checks. */
export function useCanPreviewKory(): boolean {
  const { can } = useCapabilities();
  const { data: profile } = useProfileData();
  const readOnly = useContext(ImpersonationContext)?.isReadOnly ?? false;
  const central = (profile?.tenants ?? []).some((tenant) => tenant.id === CENTRAL_TENANT_ID);
  // A read-only session runs as the previewed person, so its caps are theirs. The caller still ends it here.
  return readOnly || (can("kory.preview") && central);
}

/** The person being previewed: a read-only session's target (it runs as them), else the marker's. */
export function usePreviewedPerson(): PreviewTarget | null {
  const impersonation = useContext(ImpersonationContext);
  const target = useKoryPreview();
  if (!impersonation?.isReadOnly) return target;
  const user = impersonation.targetUser;
  return user ? { id: user.id, name: user.displayName } : null;
}

/** One tap back to the caller's own feed. `compact` fits the folded desktop rail. `onBack` ends a read-only session. */
export function PreviewBack({
  tone,
  compact = false,
  onBack,
  busy = false,
}: {
  tone: "hero" | "tray";
  compact?: boolean;
  onBack?: () => void;
  /** The return is in flight: the button says so and takes no second click. */
  busy?: boolean;
}) {
  if (!useKoryPreview() && !onBack) return null;
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-11 shrink-0 items-center justify-center rounded-full text-xs font-semibold focus-visible:outline-none focus-visible:ring-2",
        tone === "hero"
          ? "bg-celebration-foreground px-3 text-celebration focus-visible:ring-celebration-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-celebration"
          : "border bg-background px-3 text-foreground focus-visible:ring-ring",
        compact && "size-11 p-0",
      )}
      onClick={onBack ?? (() => setKoryPreview(null))}
      disabled={busy}
      aria-busy={busy}
      data-testid="kory-preview-back"
      aria-label={`Back to you. ${PREVIEW_NOTICE}.`}
      title="Back to you"
    >
      {busy ? (
        <>
          <Loader2 className={cn("size-4 animate-spin", !compact && "mr-1")} aria-hidden />
          {compact ? null : "Returning…"}
        </>
      ) : compact ? (
        <Undo2 className="size-4" aria-hidden />
      ) : (
        "Back to you"
      )}
    </button>
  );
}

/** The tray's one preview line, beside the disabled composer and on the phone bar. */
export function PreviewTrayCue() {
  const target = useKoryPreview();
  if (!target) return null;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <p
        id="kory-preview-tray-cue"
        className="line-clamp-2 min-w-0 flex-1 text-xs font-medium text-foreground"
        data-testid="kory-preview-tray-cue"
      >
        {previewingLine(target.name)}
      </p>
      <PreviewBack tone="tray" />
    </div>
  );
}

/**
 * The Overview control that previews another person's feed.
 * Hidden unless the caller holds kory.preview and belongs to Hickory Central.
 * The people list mounts only while the menu is open.
 */
// The feature-off 404 only. A missing or inactive target is also a 404, with its own detail,
// and must not fall back to the marker path.
function isNotFound(error: unknown): boolean {
  const e = error as { status?: number; message?: string } | null;
  return e?.status === 404 && e.message === "Not Found";
}

export function PreviewPill({ personaRole }: { personaRole?: string | null }) {
  const allowed = useCanPreviewKory();
  const impersonation = useContext(ImpersonationContext);
  const readOnly = impersonation?.isReadOnly ?? false;
  const { data: profile } = useProfileData();
  const target = useKoryPreview();
  const previewed = usePreviewedPerson();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // The name of the person a session switch is opening, while the request is in flight.
  const [switching, setSwitching] = useState<string | null>(null);
  const { leaving, back: backToYou } = useReturnToSelf(impersonation?.stopImpersonation);
  if (!allowed) return null;
  const selfId = profile?.id;

  const label = !previewed
    ? "You"
    : personaRole
      ? `You · ${personaRole}: ${firstName(previewed.name)}`
      : `You · ${firstName(previewed.name)}`;

  // Start a read-only session. With no session provider, or when the feature is off (404), use the marker.
  const previewPerson = async (person: PreviewTarget) => {
    if (!impersonation) {
      setKoryPreview(person);
      return;
    }
    if (switching) return;
    setSwitching(person.name);
    try {
      // On success the page reloads as the person, so the busy state stays until then.
      await impersonation.startPreview(person.id);
    } catch (error) {
      setSwitching(null);
      if (!isNotFound(error)) {
        toast.error("Couldn't start the preview. Please try again.");
        return;
      }
      setKoryPreview(person);
    }
  };

  if (readOnly) {
    return (
      <div className="flex min-w-0 items-center gap-1.5" data-preview-controls="">
        <span
          className="inline-flex h-11 min-w-0 max-w-[8.5rem] items-center rounded-full bg-celebration-foreground/10 px-3 text-xs font-semibold text-celebration-foreground ring-1 ring-celebration-foreground/30 sm:max-w-[16rem]"
          data-testid="kory-preview-pill"
          title={label}
        >
          <span className="truncate">{label}</span>
        </span>
        <PreviewBack tone="hero" onBack={() => void backToYou()} busy={leaving} />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5" data-preview-controls="">
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex h-11 min-w-11 max-w-[8.5rem] shrink items-center gap-1 rounded-full bg-celebration-foreground/10 px-3 text-xs font-semibold text-celebration-foreground ring-1 ring-celebration-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-celebration-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-celebration sm:max-w-[16rem]"
            data-testid="kory-preview-pill"
            disabled={switching !== null}
            aria-busy={switching !== null}
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-label={target ? `Previewing ${label}. Choose someone else.` : "Preview as someone else"}
            title={label}
          >
            <span className="flex min-w-0 items-center">
              {switching ? (
                <>
                  <Loader2 className="mr-1 size-3.5 shrink-0 animate-spin" aria-hidden />
                  <span className="min-w-0 truncate">{`Opening ${firstName(switching)}…`}</span>
                </>
              ) : target ? (
                <>
                  {/* A trailing space in a flex item collapses to nothing. */}
                  <span className="shrink-0">You</span>
                  <span className="shrink-0">{"\u00a0·\u00a0"}</span>
                  {personaRole ? (
                    <span className="hidden min-w-0 truncate @[380px]/hero:inline">{personaRole}</span>
                  ) : null}
                  {personaRole ? (
                    <span className="hidden shrink-0 @[380px]/hero:inline">{":\u00a0"}</span>
                  ) : null}
                  <span className="shrink-0">{firstName(target.name)}</span>
                </>
              ) : (
                "You"
              )}
            </span>
            <ChevronDown className="size-3.5 shrink-0" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[min(20rem,calc(100vw-1.5rem))] p-3">
          {open ? (
            <PersonPicker
              source="users"
              excludeIds={selfId ? [selfId] : undefined}
              selected={target ? [{ id: target.id, full_name: target.name }] : []}
              searchQuery={search}
              onSearchChange={setSearch}
              placeholder="Search by name..."
              onToggle={(person) => {
                // The server treats the caller as a normal read. Storing that id
                // would hide their widgets and disable the tray for their own feed.
                const ownRead = person.id === selfId || person.id === target?.id;
                setOpen(false);
                if (ownRead) setKoryPreview(null);
                else void previewPerson({ id: person.id, name: person.full_name });
              }}
            />
          ) : null}
        </PopoverContent>
      </Popover>
      <PreviewBack tone="hero" />
    </div>
  );
}
